import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { STICKY_PAGE_RAIL_CLASS } from '../ui/stickyPageRail';
import { acquireBodyScrollLock } from '../../utils/bodyScrollLock';
import { BuildCollectibleMini, type BuildCollectibleMiniProps } from './BuildCollectibleMini';
import { SectionRailMobileTrigger } from '../ui/SectionRailMobileTrigger';

export interface VehicleSectionNavItem {
  id: string;
  label: string;
}

export interface VehicleOwnerToolAction {
  id: string;
  label: string;
  onClick: () => void;
  /** Optional line under the label, matching Organizer / Judge nav items. */
  description?: string;
}

export type VehicleSectionNavCollectible = BuildCollectibleMiniProps;

interface VehicleSectionNavProps {
  items: VehicleSectionNavItem[];
  ownerTools?: VehicleOwnerToolAction[];
  /** Optional cue under Owner tools (e.g. Super Admin editing another user). */
  ownerToolsExtra?: React.ReactNode;
  /** Rail heading when no collectible plate is shown. */
  title?: string;
  /** Mobile open-drawer control label. */
  mobileTriggerLabel?: string;
  ownerToolsLabel?: string;
  ownerToolsHint?: string;
  /** Optional build-profile accent hex for rail tint + active states */
  accentColor?: string | null;
  /** Mini collectible plate shown above section jumps */
  collectible?: VehicleSectionNavCollectible | null;
  className?: string;
  children: React.ReactNode;
}

/** Sticky header on the build page is h-14; spy activates slightly below it. */
const SCROLL_SPY_OFFSET_PX = 96;

/**
 * Ops-style sticky sidebar for the public/owner build page.
 * Desktop: left rail sticks while the build scrolls.
 * Active item tracks scroll depth via section tops vs. the header offset.
 */
export const VehicleSectionNav: React.FC<VehicleSectionNavProps> = ({
  items,
  ownerTools = [],
  ownerToolsExtra = null,
  title = 'Build sections',
  mobileTriggerLabel,
  ownerToolsLabel = 'Owner tools',
  ownerToolsHint = 'Only you see these',
  accentColor = null,
  collectible = null,
  className = '',
  children,
}) => {
  const reactId = useId();
  const navId = `build-sections-${reactId.replace(/:/g, '')}`;
  // Stabilize on id/label content so parent re-creates of `items` do not rebind scroll spy.
  const itemsKey = items.map((item) => `${item.id}:${item.label}`).join('|');
  const visible = useMemo(
    () => items.filter((item) => item.id && item.label),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by itemsKey
    [itemsKey]
  );
  const [activeId, setActiveId] = useState<string>(visible[0]?.id || '');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!visible.some((item) => item.id === activeId) && visible[0]) {
      setActiveId(visible[0].id);
    }
  }, [activeId, visible]);

  const resolveActiveSection = useCallback(() => {
    if (visible.length === 0) return;
    let current = visible[0].id;
    for (const item of visible) {
      const el = document.getElementById(item.id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= SCROLL_SPY_OFFSET_PX) {
        current = item.id;
      }
    }
    setActiveId((prev) => (prev === current ? prev : current));
  }, [visible]);

  useEffect(() => {
    if (visible.length === 0) return undefined;

    let frame = 0;
    const onScrollOrResize = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(resolveActiveSection);
    };

    // Sections mount with the page content; retry once after paint so late IDs are found.
    resolveActiveSection();
    const settle = window.setTimeout(resolveActiveSection, 120);

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.clearTimeout(settle);
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [itemsKey, resolveActiveSection, visible.length]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const releaseScrollLock = acquireBodyScrollLock();
    return () => {
      document.removeEventListener('keydown', onKey);
      releaseScrollLock();
    };
  }, [mobileOpen]);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el || typeof window === 'undefined') return;
    setActiveId(id);
    setMobileOpen(false);
    // Prefer scrollTo over scrollIntoView + hash. Setting location.hash (or
    // replaceState to #id) makes the browser re-snap to the section top whenever
    // timeline/content layout shifts — the desktop flicker users hit at Timeline.
    const top =
      el.getBoundingClientRect().top + window.scrollY - SCROLL_SPY_OFFSET_PX + 8;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    if (window.history?.replaceState && window.location.hash) {
      const { pathname, search } = window.location;
      window.history.replaceState(null, '', `${pathname}${search}`);
    }
  }, []);

  const showNav = visible.length >= 2 || ownerTools.length > 0;
  const activeIndex = Math.max(
    0,
    visible.findIndex((item) => item.id === activeId)
  );
  const progress =
    visible.length <= 1 ? 1 : (activeIndex + 1) / visible.length;

  const activeStyle = accentColor
    ? {
        backgroundColor: `${accentColor}22`,
        borderColor: `${accentColor}66`,
        color: accentColor,
      }
    : undefined;

  const hoverAccentStyle = accentColor
    ? ({
        ['--rail-hover-bg' as string]: `${accentColor}14`,
      } as React.CSSProperties)
    : undefined;

  const railAccentStyle = accentColor
    ? ({
        ['--build-accent' as string]: accentColor,
      } as React.CSSProperties)
    : undefined;

  const activeLabel = visible.find((i) => i.id === activeId)?.label;
  const navLabel = collectible?.title ? `${collectible.title} sections` : title;
  const triggerLabel = mobileTriggerLabel || (collectible?.title ? collectible.title : title === 'Build sections' ? 'Sections' : title);

  const panel = (
    <div className="flex flex-col h-full min-h-0">
      <div className="build-section-rail__header flex items-start justify-between gap-2 px-3 pt-3 pb-2 shrink-0">
        <div className="min-w-0 flex-1">
          {collectible ? (
            <>
              <h2 className="sr-only">{collectible.title}</h2>
              <BuildCollectibleMini {...collectible} />
              <p className="build-section-rail__hint text-[11px] mt-2 px-0.5 leading-snug">
                {activeLabel ? `Viewing ${activeLabel}` : 'Jump to a section'}
              </p>
            </>
          ) : (
            <>
              <h2 className="font-display text-lg font-semibold tracking-tight text-white px-1">
                {title}
              </h2>
              <p className="build-section-rail__hint text-[11px] mt-0.5 leading-snug px-1">
                {activeLabel ? `Viewing ${activeLabel}` : 'Jump to a section'}
              </p>
            </>
          )}
        </div>
        <button
          type="button"
          className="lg:hidden p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-white cursor-pointer rounded-lg shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25"
          aria-label="Close sections"
          onClick={() => setMobileOpen(false)}
        >
          <XMarkIcon className="w-5 h-5" aria-hidden />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-4 space-y-5">
        {visible.length > 0 ? (
          <div>
            <div className="flex items-center justify-between px-2 mb-1.5">
              <p className="build-section-rail__label py-1 text-[11px] font-semibold uppercase tracking-wider">
                Sections
              </p>
              <p
                className="build-section-rail__count text-[11px] tabular-nums"
                aria-hidden
              >
                {activeIndex + 1}/{visible.length}
              </p>
            </div>
            {/* Scroll-depth rail: fills as the user moves through sections. */}
            <div
              className="relative mb-2 mx-2 h-1 rounded-full bg-white/[0.06] overflow-hidden"
              aria-hidden
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-orange-400/80 transition-[width] duration-300 ease-out"
                style={{
                  width: `${Math.round(progress * 100)}%`,
                  ...(accentColor ? { backgroundColor: accentColor } : {}),
                }}
              />
            </div>
            <ul className="mt-1 space-y-0.5 list-none" style={hoverAccentStyle}>
              {visible.map((item) => {
                const active = item.id === activeId;
                return (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(item.id);
                      }}
                      className={`build-section-rail__link relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 min-h-[44px] cursor-pointer border ${
                        active
                          ? accentColor
                            ? 'border'
                            : 'bg-white/[0.06] text-white border-white/20'
                          : 'text-gray-300 border-transparent'
                      }`}
                      style={active ? activeStyle : undefined}
                      aria-current={active ? 'location' : undefined}
                    >
                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${active ? '' : 'bg-white/20'}`}
                        style={
                          active
                            ? { backgroundColor: accentColor || '#e5e7eb' }
                            : undefined
                        }
                        aria-hidden
                      />
                      <span className="truncate">{item.label}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {ownerTools.length > 0 ? (
          <div>
            <p className="build-section-rail__label px-2 py-1 text-[11px] font-semibold uppercase tracking-wider">
              {ownerToolsLabel}
            </p>
            <p className="build-section-rail__hint px-2 text-[11px] mb-1.5">{ownerToolsHint}</p>
            <ul className="mt-1 space-y-0.5 list-none" style={hoverAccentStyle}>
              {ownerTools.map((tool) => (
                <li key={tool.id}>
                  <button
                    type="button"
                    data-testid={`vehicle-tool-${tool.id}`}
                    onClick={() => {
                      setMobileOpen(false);
                      tool.onClick();
                    }}
                    className="build-section-rail__link w-full text-left flex flex-col items-start justify-center px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 border border-transparent transition-colors duration-200 min-h-[44px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25"
                  >
                    <span>{tool.label}</span>
                    {tool.description ? (
                      <span className="text-[11px] font-normal text-gray-500 mt-0.5 leading-snug">
                        {tool.description}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
            {ownerToolsExtra}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (!showNav) {
    return <div className="w-full">{children}</div>;
  }

  const mobileDrawer =
    mobileOpen && typeof document !== 'undefined'
      ? createPortal(
          <>
            <button
              type="button"
              className="fixed inset-0 z-[80] bg-black/55 lg:hidden cursor-pointer"
              aria-label="Close sections overlay"
              onClick={() => setMobileOpen(false)}
            />
            <aside
              id={navId}
              className={`build-section-rail fixed inset-y-0 left-0 z-[90] flex flex-col w-[min(20rem,88vw)] max-w-full overflow-hidden shadow-2xl shadow-black/50 lg:hidden${
                accentColor ? ' build-section-rail--accent' : ''
              }`}
              style={railAccentStyle}
              aria-label={navLabel}
              role="dialog"
              aria-modal="true"
            >
              {panel}
            </aside>
          </>,
          document.body
        )
      : null;

  return (
    <div className={`w-full ${className}`.trim()}>
      <div
        className={`lg:hidden mb-4 sticky top-16 z-30 -mx-1 px-1 py-2 backdrop-blur-md${
          accentColor ? ' build-section-rail-mobile-bar--accent' : ' bg-[#0a0a0a]/90'
        }`}
        style={railAccentStyle}
      >
        <SectionRailMobileTrigger
          title={triggerLabel}
          subtitle={activeLabel || null}
          expanded={mobileOpen}
          controlsId={navId}
          onClick={() => setMobileOpen(true)}
          className={accentColor ? 'build-section-rail-mobile-trigger' : ''}
        />
      </div>

      <div className="flex gap-0 lg:gap-8 items-start">
        {/* Desktop rail stays in-flow. Mobile drawer is portaled so page
            overflow-x-clip cannot trap position:fixed off-screen. */}
        <aside
          className={`build-section-rail ${STICKY_PAGE_RAIL_CLASS}${
            accentColor ? ' build-section-rail--accent' : ''
          }`}
          style={railAccentStyle}
          aria-label={navLabel}
        >
          {panel}
        </aside>

        {mobileDrawer}

        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
};

export default VehicleSectionNav;
